import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { fetch } from '@ngrx/router-store/data-persistence';
import { catchError, filter, map, merge, of, switchMap, tap } from 'rxjs';

import { AccountActions } from '@lockton-apps/auth/action';
import {
  BrokerCreateUpdateActions,
  BrokerDetailsActions,
  BrokerListActions,
  MyBrokersActions,
} from '@lockton-apps/brokers/action';
import { UsersTransformationsService } from '@lockton-apps/client-gateway/transformations';
import {
  ClientBrokersActions,
  ClientEnquiriesActions,
} from '@lockton-apps/clients/actions';
import {
  DashboardBrokerActions,
  DashboardBrokerLsosActions,
  DashboardBrokerNorwayActions,
  DashboardContactActions,
} from '@lockton-apps/dashboard/action';
import {
  PolicyCreateUpdateActions,
  PolicyDetailsActions,
} from '@lockton-apps/policies/action';
import { AuthApiActions } from '@lockton-apps/shared/action/auth';
import { PoliciesApiActions } from '@lockton-apps/shared/action/policies';
import { UsersApiActions } from '@lockton-apps/shared/action/users';
import {
  AuthActions,
  AuthSelectors,
} from '@lockton-apps/shared/data-access/auth';
import { PortalConfigSelectors } from '@lockton-apps/shared/data-access/portal-config';
import {
  ClientRouterSelectors,
  UserRouterSelectors,
} from '@lockton-apps/shared/data-access/router';
import { FeedbackService } from '@lockton-apps/shared/ui/lui';
import {
  UserCreateUpdateActions,
  UserDetailsActions,
  UserListActions,
} from '@lockton-apps/users/actions';

import { UsersService } from '../services/users.service';
import * as UsersSelectors from './users.selectors';
import { Users2Service } from '../services/users2.service';

@Injectable()
export class UsersEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  readonly genericFailureMessage = $localize`Something has gone wrong. Please try again`;

  // User List
  loadUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        UserListActions.init,
        BrokerListActions.init,
        PolicyCreateUpdateActions.init
      ),
      fetch({
        run: () => {
          return this.usersService
            .all()
            .pipe(
              map((res) => UsersApiActions.loadUsersSuccess({ users: res }))
            );
        },
        onError: (_, error) => {
          console.error('Error', error);
          return UsersApiActions.loadUsersFailure({ error });
        },
      })
    )
  );

  loadByBrokerT2Code$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        DashboardBrokerActions.init,
        DashboardBrokerLsosActions.init,
        DashboardBrokerNorwayActions.init
      ),
      switchMap(() =>
        this.usersService
          .loadByBrokerT2Code()
          .pipe(
            map((users) =>
              UsersApiActions.loadBrokersByT2CodeSuccess({ users })
            )
          )
      )
    )
  );

  // User List
  loadUsersForAuthenticatedUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DashboardContactActions.init, MyBrokersActions.init),
      switchMap(() =>
        this.usersService2
          .brokersForAuthenticatedContact()
          .pipe(
            map((users) => UsersApiActions.loadClientBrokersSuccess({ users }))
          )
      )
    )
  );

  loadUser$ = createEffect(() =>
    merge(
      this.actions$.pipe(
        ofType(
          UserDetailsActions.init,
          UserCreateUpdateActions.initUpdate,
          BrokerDetailsActions.init,
          BrokerCreateUpdateActions.initUpdate
        ),
        concatLatestFrom(() =>
          this.store.select(UserRouterSelectors.selectRoutedId)
        ),
        map(([, userId]) => userId)
      ),
      this.actions$.pipe(
        ofType(PoliciesApiActions.loadPolicyForPolicyDetailsSuccess),
        filter(({ policy }) => !!policy.specialistUserId),
        map(({ policy }) => policy.specialistUserId)
      ),
      this.actions$.pipe(
        ofType(AccountActions.init),
        concatLatestFrom(() => this.store.select(AuthSelectors.getNameId)),
        map(([, userId]) => userId)
      ),
      this.actions$.pipe(
        ofType(
          AuthApiActions.loginSuccess,
          AuthActions.initSuccess,
          AuthApiActions.signUpSuccess
        ),
        concatLatestFrom(() => this.store.select(AuthSelectors.getRole)),
        map(([{ session }]) => session.nameid)
      )
    ).pipe(
      switchMap((userId) => {
        if (!userId) {
          throw new Error('No user Id');
        }
        return this.usersService.load(userId).pipe(
          map((user) => UsersApiActions.loadUserSuccess({ user })),
          catchError((error) => of(UsersApiActions.loadUserFailure({ error })))
        );
      })
    )
  );

  deleteUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        UserDetailsActions.userDeleted,
        BrokerDetailsActions.brokerDeleted
      ),
      fetch({
        run: ({ user }) => {
          return this.usersService.delete(user).pipe(
            map(() => {
              return UsersApiActions.userDeletedSuccess({
                user: user,
              });
            })
          );
        },
        onError: (_, error) => {
          console.error('Error', error);
          return UsersApiActions.userDeletedFailure({
            error: this.genericFailureMessage,
          });
        },
      })
    )
  );

  enableUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        BrokerDetailsActions.brokerEnabled,
        UserDetailsActions.userEnabled
      ),
      concatLatestFrom(() =>
        this.store.select(UsersSelectors.selectRoutedUser)
      ),
      switchMap(([, user]) => {
        if (!user) {
          throw new Error('No user selected');
        }

        return this.usersService
          .enable(user.userId)
          .pipe(map(() => UsersApiActions.userEnabledSuccess({ user })));
      })
    )
  );

  enableUserSuccessMessage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UsersApiActions.userEnabledSuccess),
        tap(() => {
          this.feedbackService.subtleMessage(
            $localize`Account successfully enabled`,
            { type: 'success' }
          );
        })
      ),
    { dispatch: false }
  );

  userDeletedSuccessMessage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UsersApiActions.userDeletedSuccess),
        tap(() => {
          this.feedbackService.subtleMessage(
            $localize`Account successfully deleted`,
            { type: 'success' }
          );
        })
      ),
    { dispatch: false }
  );

  /* User Create Update View */
  userCreateUpdateInit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserCreateUpdateActions.init),
      concatLatestFrom(() =>
        this.store.select(UserRouterSelectors.selectRoutedId)
      ),
      switchMap(([, userId]) => {
        return userId
          ? of(UserCreateUpdateActions.initUpdate())
          : of(UserCreateUpdateActions.initCreate());
      })
    )
  );

  addUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserCreateUpdateActions.add),
      map((action) => ({
        ...action,
        user: this.transformService.userCreate(action.userModel),
      })),
      switchMap(({ user }) =>
        this.usersService.create(user).pipe(
          map((user) => UsersApiActions.addSuccess({ user })),
          catchError((error) => of(UsersApiActions.addFailure({ error })))
        )
      )
    )
  );

  addBroker$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BrokerCreateUpdateActions.added),
      map((action) => ({
        ...action,
        user: this.transformService.userCreate(action.userModel),
      })),
      switchMap(({ user }) =>
        this.usersService.create(user).pipe(
          map((user) => UsersApiActions.addBrokerSuccess({ user })),
          catchError((error) => of(UsersApiActions.addBrokerFailure({ error })))
        )
      )
    )
  );

  addBrokerSuccessMessageAndNavigation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UsersApiActions.addBrokerSuccess),
        tap(({ user }) => {
          this.feedbackService.subtleMessage(
            $localize`The broker ${user.givenName} has been successfully created.`,
            { type: 'success' }
          );
          this.router.navigate(['/brokers', user.userId]);
        })
      ),
    { dispatch: false }
  );

  addUserSuccessMessageAndNavigation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UsersApiActions.addSuccess),
        tap(({ user }) => {
          this.feedbackService.subtleMessage(
            $localize`The user ${user.displayName} was successfully created.`,
            { type: 'success' }
          );
          this.router.navigate(['/users', user.userId]);
        })
      ),
    { dispatch: false }
  );

  updateUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserCreateUpdateActions.update),
      concatLatestFrom(() =>
        this.store.pipe(select(UserRouterSelectors.selectRoutedId))
      ),
      map(([{ userModel }, userId]) => {
        return this.transformService.userUpdate(userModel, userId as string);
      }),
      switchMap((user) =>
        this.usersService.update(user).pipe(
          map((user) => UsersApiActions.updateSuccess({ user })),
          catchError((error) => of(UsersApiActions.updateFailure({ error })))
        )
      )
    )
  );

  updateBroker$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BrokerCreateUpdateActions.updated),
      concatLatestFrom(() =>
        this.store.pipe(select(UserRouterSelectors.selectRoutedId))
      ),
      map(([{ userModel }, userId]) => {
        return this.transformService.userUpdate(userModel, userId as string);
      }),
      switchMap((user) =>
        this.usersService.update(user).pipe(
          map((user) => UsersApiActions.updateBrokerSuccess({ user })),
          catchError((error) =>
            of(UsersApiActions.updateBrokerFailure({ error }))
          )
        )
      )
    )
  );

  updateBrokerSuccessMessageAndNavigation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UsersApiActions.updateBrokerSuccess),
        tap(({ user }) => {
          this.feedbackService.subtleMessage(
            $localize`The broker ${user.givenName} has been successfully updated.`,
            { type: 'success' }
          );
          this.router.navigate(['/brokers', user.userId]);
        })
      ),
    { dispatch: false }
  );

  updateUserSuccessMessage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UsersApiActions.updateSuccess),
        tap(({ user }) => {
          this.feedbackService.subtleMessage(
            $localize`The user ${user.givenName} has been successfully updated.`,
            { type: 'success' }
          );
          this.router.navigate(['/users', user.userId]);
        })
      ),
    { dispatch: false }
  );

  loadClientBrokers$ = createEffect(() =>
    merge(
      this.actions$.pipe(
        ofType(ClientBrokersActions.init, ClientEnquiriesActions.init),
        concatLatestFrom(() =>
          this.store.select(ClientRouterSelectors.selectRoutedId)
        ),
        map(([, clientId]) => clientId)
      ),
      // Policy create
      this.actions$.pipe(
        ofType(PolicyCreateUpdateActions.init),
        concatLatestFrom(() =>
          this.store.select(ClientRouterSelectors.selectQueriedId)
        ),
        map(([, clientId]) => clientId),
        filter(Boolean)
      ),
      // Policy update
      this.actions$.pipe(
        ofType(PolicyCreateUpdateActions.init),
        switchMap(() =>
          this.actions$.pipe(
            ofType(PoliciesApiActions.loadPolicySuccess),
            map(({ policy }) => policy.policyClients[0].id)
          )
        )
      ),
      this.actions$.pipe(
        ofType(PolicyDetailsActions.init),
        switchMap(() =>
          this.actions$.pipe(
            ofType(PoliciesApiActions.loadPolicyForPolicyDetailsSuccess),
            concatLatestFrom(() =>
              this.store.select(
                PortalConfigSelectors.selectPortalConfigDefinition
              )
            ),
            filter(
              ([{ policy }, portalConfigDefinition]) =>
                portalConfigDefinition &&
                portalConfigDefinition[policy.portalId]?.other
                  ?.policyUsesClientBrokers
            )
          )
        ),
        map(([{ policy }]) => policy.policyClients[0].id)
      )
    ).pipe(
      switchMap((clientId) => {
        if (!clientId) {
          throw new Error('No user Id');
        }
        return this.usersService.allForClientId(clientId).pipe(
          map((users) => UsersApiActions.loadClientBrokersSuccess({ users })),
          catchError((error) =>
            of(UsersApiActions.loadClientBrokersFailure({ error }))
          )
        );
      })
    )
  );

  constructor(
    private usersService: UsersService,
    private usersService2: Users2Service,
    private feedbackService: FeedbackService,
    private router: Router,
    private transformService: UsersTransformationsService
  ) {}
}
